import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { setMemberToken, clearMemberToken } from '../lib/memberToken';

const API = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = 'vaad_visitor_id_v1';
const DECLINED_KEY = 'vaad_access_declined_at_v1';
const PING_KEY = 'vaad_access_ping_at_v1';
const REPROMPT_MS = 14 * 24 * 3600 * 1000;

const AccessContext = createContext(null);

const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return 'v_anon';
  }
};

// The SW keeps API responses (speakers, recordings, topics…) in a
// stale-while-revalidate cache. A tier change must drop that cache or
// the previous tier's data lingers for a visit.
const clearApiCaches = async () => {
  try {
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.endsWith('-api')).map((k) => caches.delete(k)));
  } catch { /* noop */ }
};

export const AccessProvider = ({ children }) => {
  const [state, setState] = useState({ loading: true, tier: 'guest', email: null, name: null, picture: null, method: null, demoted: false });
  const [promptOpen, setPromptOpen] = useState(false);
  const autoPrompted = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/access/me`, { credentials: 'include', cache: 'no-store' });
      if (!r.ok) throw new Error('bad');
      const d = await r.json();
      setState({ loading: false, ...d });
      return d;
    } catch {
      setState((s) => ({ ...s, loading: false }));
      return null;
    }
  }, []);

  useEffect(() => {
    refresh().then(() => {
      // Landing from the native Google redirect: drop guest-tier API caches once.
      if (window.__vaadFreshSignin) {
        window.__vaadFreshSignin = false;
        clearApiCaches();
      }
    });
  }, [refresh]);

  // Native sign-in error handed back on the URL by /api/access/google/native.
  const [signinError, setSigninError] = useState(() => {
    try {
      const u = new URL(window.location.href);
      const e = u.searchParams.get('signin_error');
      if (!e) return '';
      u.searchParams.delete('signin_error');
      window.history.replaceState(null, '', u.pathname + u.search + u.hash);
      return e;
    } catch { return ''; }
  });
  useEffect(() => { if (signinError) setPromptOpen(true); }, [signinError]);

  // First-visit prompt for guests (re-offered gently every 14 days).
  useEffect(() => {
    if (state.loading || state.tier !== 'guest' || autoPrompted.current) return;
    if (window.location.pathname.startsWith('/admin')) return;
    autoPrompted.current = true;
    let declinedAt = 0;
    try { declinedAt = Number(localStorage.getItem(DECLINED_KEY) || 0); } catch { /* noop */ }
    if (!declinedAt || Date.now() - declinedAt > REPROMPT_MS) {
      const t = setTimeout(() => setPromptOpen(true), 1800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state.loading, state.tier]);

  // Daily heartbeat so the admin can see how many people are on the lower tier.
  useEffect(() => {
    if (state.loading || state.tier !== 'guest' || state.email) return;
    let last = 0;
    try { last = Number(localStorage.getItem(PING_KEY) || 0); } catch { /* noop */ }
    if (Date.now() - last < 24 * 3600 * 1000) return;
    try { localStorage.setItem(PING_KEY, String(Date.now())); } catch { /* noop */ }
    fetch(`${API}/api/access/ping`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_id: getVisitorId() }),
    }).catch(() => {});
  }, [state.loading, state.tier, state.email]);

  const signInWithEmail = useCallback(async (email, name) => {
    const r = await fetch(`${API}/api/access/email-signin`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name: name || null, visitor_id: getVisitorId() }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      const detail = err.detail;
      throw new Error(Array.isArray(detail) ? 'Please enter a valid email address.' : (detail || 'Could not sign you in. Please try again.'));
    }
    const d = await r.json().catch(() => ({}));
    if (d.member_token) setMemberToken(d.member_token);
    try { localStorage.removeItem(DECLINED_KEY); } catch { /* noop */ }
    await clearApiCaches();
    setPromptOpen(false);
    window.location.reload();
  }, []);

  const decline = useCallback(async () => {
    try { localStorage.setItem(DECLINED_KEY, String(Date.now())); } catch { /* noop */ }
    setPromptOpen(false);
    fetch(`${API}/api/access/decline`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_id: getVisitorId() }),
    }).catch(() => {});
  }, []);

  const signOut = useCallback(async () => {
    await fetch(`${API}/api/access/signout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    clearMemberToken();
    await clearApiCaches();
    window.location.reload();
  }, []);

  const value = useMemo(() => ({
    ...state,
    isGuest: !state.loading && state.tier !== 'full',
    isFull: state.tier === 'full',
    promptOpen,
    signinError,
    clearSigninError: () => setSigninError(''),
    openPrompt: () => setPromptOpen(true),
    closePrompt: () => setPromptOpen(false),
    signInWithEmail,
    decline,
    signOut,
    refresh,
  }), [state, promptOpen, signinError, signInWithEmail, decline, signOut, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export const useAccess = () => {
  const ctx = useContext(AccessContext);
  return ctx || {
    loading: false, tier: 'full', isGuest: false, isFull: true, email: null,
    promptOpen: false, signinError: '', clearSigninError: () => {}, openPrompt: () => {}, closePrompt: () => {},
    signInWithEmail: async () => {}, decline: () => {}, signOut: async () => {}, refresh: async () => {},
  };
};
